import { useState, useEffect, useRef } from 'react'
import { Search, Filter } from 'lucide-react'
import { DeviceFilters } from '../../types/device-filters'
import { Select } from '../ui/Select'

interface DeviceSearchFilterProps {
  searchQuery: string
  onSearchChange: (query: string) => void
  filters: DeviceFilters
  onFilterChange: (key: string, value: string) => void
  onClearFilters: () => void
}

export const DeviceSearchFilter = ({
  searchQuery,
  onSearchChange,
  filters,
  onFilterChange,
  onClearFilters
}: DeviceSearchFilterProps) => {
  const [showFilterPopover, setShowFilterPopover] = useState(false)
  const filterRef = useRef<HTMLDivElement>(null)

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchChange(e.target.value)
  }

  const handleToggleFilterPopover = () => {
    setShowFilterPopover(!showFilterPopover)
  }

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterPopover(false)
      }
    }

    if (showFilterPopover) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showFilterPopover])

  return (
    <div className="flex items-center justify-between">
      {/* Search Bar */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder="Search devices..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-red-500 focus:border-red-500 text-sm"
        />
      </div>
      
      {/* Filter Button */}
      <div className="relative" ref={filterRef}>
        <button 
          onClick={handleToggleFilterPopover}
          className="flex items-center px-6 py-3 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filter
        </button>
        
        {/* Filter Popover */}
        {showFilterPopover && (
          <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 min-w-[200px]">
            <div className="p-4 space-y-4">
              {/* Status Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Status
                </label>
                <Select
                  value={filters.status}
                  onChange={(e) => onFilterChange('status', e.target.value)}
                  className="w-full"
                >
                  <option value="">All Statuses</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                  <option value="warning">Warning</option>
                  <option value="error">Error</option>
                </Select>
              </div>

              {/* Organization Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Organization
                </label>
                <Select
                  value={filters.organization}
                  onChange={(e) => onFilterChange('organization', e.target.value)}
                  className="w-full"
                >
                  <option value="">All Organizations</option>
                  <option value="WCD">WCD</option>
                </Select>
              </div>

              {/* Device Type Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Device Type
                </label>
                <Select
                  value={filters.deviceType}
                  onChange={(e) => onFilterChange('deviceType', e.target.value)}
                  className="w-full"
                >
                  <option value="">All Types</option>
                  <option value="Windows">Windows</option>
                  <option value="macOS">macOS</option>
                  <option value="Linux">Linux</option>
                  <option value="Other">Other</option>
                </Select>
              </div>

              {/* Tickets Filter */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Tickets
                </label>
                <Select
                  value={filters.hasTickets}
                  onChange={(e) => onFilterChange('hasTickets', e.target.value)}
                  className="w-full"
                >
                  <option value="">All Devices</option>
                  <option value="with-tickets">With Tickets</option>
                  <option value="without-tickets">Without Tickets</option>
                </Select>
              </div>

              {/* Clear Filters Button */}
              <div className="pt-2 border-t border-gray-200">
                <button
                  onClick={onClearFilters}
                  className="w-full text-xs text-gray-600 hover:text-gray-800 py-1"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
